import { useCallback, useEffect, useState } from "react";

/**
 * The customer's name as the operator corrected it, shared by every flyout
 * tab that puts it on a document.
 *
 * The questionnaire carries no customer name - only the appliance's display
 * name, which is what each tab used to prefill on its own. Correcting it in the
 * offer dialog therefore left the documentation tab on the old name, and the
 * documentation attached to the approval named a different customer than the
 * offer beside it. One value per appliance, for as long as the page is open,
 * keeps them together; the display name stays the starting point.
 */
const names = new Map<string, string>();
const listeners = new Set<() => void>();

export const useCustomerName = (
  applianceId: string | undefined,
  fallback: string,
): [string, (value: string) => void] => {
  const [, rerender] = useState(0);

  useEffect(() => {
    const listener = () => rerender((n) => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const key = applianceId ?? "";
  const value = names.has(key) ? (names.get(key) as string) : fallback;

  const setValue = useCallback(
    (next: string) => {
      names.set(key, next);
      listeners.forEach((listener) => listener());
    },
    [key],
  );

  return [value, setValue];
};
